import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '@heroui/react';
import { apiFetch } from '@/lib/api';
import { useUserStore } from '@/store/userSlice';
import type { Client, ClientDocument, LoginInfo, WorkerFieldDefinition } from '@/lib/types';

/** admin — карточка клиента в CMS (/clients/:id); self — форма данных
 *  самого клиента после саморегистрации (/profile, эндпоинты /me). */
export type ClientEditMode = 'admin' | 'self';

export interface ClientFormValues {
  title: string;
  legal_form: string;
  primary_color: string;
  phone: string;
  email: string;
  inn: string;
  kpp: string;
  bik: string;
  checking_account: string;
  correspondent_account: string;
  address: string;
  general_director: string;
  worker_fields: Record<string, 'required' | 'optional'>;
}

const EMPTY_FORM: ClientFormValues = {
  title: '',
  legal_form: 'ooo',
  primary_color: '',
  phone: '',
  email: '',
  inn: '',
  kpp: '',
  bik: '',
  checking_account: '',
  correspondent_account: '',
  address: '',
  general_director: '',
  worker_fields: {},
};

async function readError(res: Response): Promise<string> {
  const data = (await res.json().catch(() => null)) as { error?: string } | null;
  return data?.error ?? `Ошибка запроса (${res.status})`;
}

export function useClientEditPage(mode: ClientEditMode = 'admin') {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const setConfirmed = useUserStore((s) => s.setConfirmed);
  // /clients/new — создание, /clients/:id — редактирование;
  // self-режим работает через /me (id из clients.user_id на бэкенде)
  const clientId = mode === 'admin' && id && id !== 'new' ? id : null;

  /** База запросов карточки: self — всегда /me, admin — по id клиента. */
  const cardBase = (clientId: string | null) =>
    mode === 'self' ? '/api/v1/admin/clients/me' : `/api/v1/admin/clients/${clientId}`;

  const [client, setClient] = useState<Client | null>(null);
  const [documents, setDocuments] = useState<ClientDocument[]>([]);
  const [fieldCatalog, setFieldCatalog] = useState<WorkerFieldDefinition[]>([]);
  const [form, setForm] = useState<ClientFormValues>(EMPTY_FORM);
  const [login, setLogin] = useState<LoginInfo | null>(null);
  const [loginForm, setLoginForm] = useState({ password: '', enabled: true });
  const [isLoading, setIsLoading] = useState(Boolean(clientId) || mode === 'self');
  const [notFound, setNotFound] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isSavingLogin, setIsSavingLogin] = useState(false);

  useEffect(() => {
    void apiFetch('/api/v1/admin/clients/worker-field-definitions')
      .then((res) => (res.ok ? res.json() : []))
      .then((catalog: WorkerFieldDefinition[]) => setFieldCatalog(catalog))
      .catch(() => setFieldCatalog([]));
  }, []);

  /**
   * `silent` — обновить данные без полноэкранного «Загрузка…»
   * (для действий на открытой карточке: тоггл, лого, документы).
   */
  const fetchClient = useCallback(
    async (options?: { silent?: boolean }) => {
      if (mode !== 'self' && !clientId) return;
      const silent = options?.silent ?? false;
      if (!silent) setIsLoading(true);
      setNotFound(false);
      try {
        const res = await apiFetch(cardBase(clientId));
        if (res.status === 404) {
          setNotFound(true);
          return;
        }
        if (!res.ok) throw new Error(await readError(res));
        const data = (await res.json()) as Client & { documents?: ClientDocument[] };
        setClient(data);
        setDocuments(data.documents ?? []);
        setForm({
          title: data.title,
          legal_form: data.legalForm,
          primary_color: data.primaryColor ?? '',
          phone: data.phone ?? '',
          email: data.email ?? '',
          inn: data.inn ?? '',
          kpp: data.kpp ?? '',
          bik: data.bik ?? '',
          checking_account: data.checkingAccount ?? '',
          correspondent_account: data.correspondentAccount ?? '',
          address: data.address ?? '',
          general_director: data.generalDirector ?? '',
          worker_fields: data.workerFields ?? {},
        });
      } catch (e) {
        toast.danger(e instanceof Error ? e.message : 'Не удалось загрузить клиента');
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [clientId, mode],
  );

  useEffect(() => {
    void fetchClient();
  }, [fetchClient]);

  // Карточка «Логин» — отдельный вызов бэкенда; null = логина ещё нет.
  // Только в админ-режиме: клиент не управляет своим логином.
  const fetchLogin = useCallback(async () => {
    if (mode !== 'admin' || !clientId) return;
    try {
      const res = await apiFetch(`/api/v1/admin/clients/${clientId}/login`);
      if (!res.ok) throw new Error(await readError(res));
      const data = (await res.json()) as { login: LoginInfo | null };
      setLogin(data.login);
      if (data.login) {
        setLoginForm({ password: '', enabled: data.login.enabled });
      }
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось загрузить логин');
    }
  }, [clientId]);

  useEffect(() => {
    void fetchLogin();
  }, [fetchLogin]);

  const setLoginField = (key: 'password' | 'enabled', value: string | boolean) =>
    setLoginForm((prev) => ({ ...prev, [key]: value }));

  /** Создание логина: у клиента его ещё нет (жёсткая привязка, один логин). */
  const handleCreateLogin = async () => {
    if (!clientId || isSavingLogin) return;
    if (!loginForm.password) {
      toast.danger('Укажите пароль');
      return;
    }
    setIsSavingLogin(true);
    try {
      const res = await apiFetch(`/api/v1/admin/clients/${clientId}/login`, {
        method: 'POST',
        body: JSON.stringify({ password: loginForm.password }),
      });
      if (!res.ok) throw new Error(await readError(res));
      const data = (await res.json()) as LoginInfo;
      setLogin(data);
      setLoginForm((prev) => ({ ...prev, password: '' }));
      toast.success('Логин создан');
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось создать логин');
    } finally {
      setIsSavingLogin(false);
    }
  };

  /** «Обновить» в карточке «Логин»: свой PATCH, независимо от анкеты. Login read-only. */
  const handleUpdateLogin = async () => {
    if (!clientId || !login || isSavingLogin) return;
    setIsSavingLogin(true);
    try {
      const payload: Record<string, unknown> = { enabled: loginForm.enabled };
      if (loginForm.password) payload.password = loginForm.password;
      const res = await apiFetch(`/api/v1/admin/clients/${clientId}/login`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await readError(res));
      const data = (await res.json()) as LoginInfo;
      setLogin(data);
      setLoginForm((prev) => ({ ...prev, password: '' }));
      toast.success('Логин обновлён');
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось обновить логин');
    } finally {
      setIsSavingLogin(false);
    }
  };

  const setField = <K extends keyof ClientFormValues>(key: K, value: ClientFormValues[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    const isSelf = mode === 'self';
    // В self-режиме все поля опциональны: название нужно только админу
    if (!isSelf && !form.title.trim()) {
      toast.danger('Укажите название организации');
      return;
    }
    setIsSaving(true);
    try {
      const payload: Record<string, unknown> = {
        legal_form: form.legal_form,
        worker_fields: form.worker_fields,
      };
      // Пустое название в self-режиме не отправляем (остаётся заглушка)
      if (form.title.trim()) payload.title = form.title.trim();
      for (const key of [
        'primary_color',
        'phone',
        'inn',
        'kpp',
        'bik',
        'checking_account',
        'correspondent_account',
        'address',
        'general_director',
      ] as const) {
        const value = form[key].trim();
        payload[key] = value === '' ? null : value;
      }
      payload.email = form.email.trim() === '' ? null : form.email.trim();

      const res = isSelf
        ? await apiFetch('/api/v1/admin/clients/me', {
            method: 'PATCH',
            body: JSON.stringify(payload),
          })
        : clientId
          ? await apiFetch(`/api/v1/admin/clients/${clientId}`, {
              method: 'PATCH',
              body: JSON.stringify(payload),
            })
          : await apiFetch('/api/v1/admin/clients', {
              method: 'POST',
              body: JSON.stringify(payload),
            });
      if (!res.ok) throw new Error(await readError(res));

      if (isSelf) {
        // Любое успешное сохранение подтверждает онбординг (confirmed=true
        // ставит бэкенд); первый сейв ведёт на дашборд
        const wasConfirmed = user?.confirmed ?? false;
        setConfirmed(true);
        toast.success('Данные сохранены');
        if (!wasConfirmed) {
          navigate('/dashboard', { replace: true });
          return;
        }
        await fetchClient({ silent: true });
        return;
      }

      const saved = (await res.json()) as Client;
      toast.success(clientId ? 'Клиент обновлён' : 'Клиент создан');
      if (!clientId) {
        navigate(`/clients/${saved.id}`, { replace: true });
      } else {
        await fetchClient({ silent: true });
      }
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось сохранить клиента');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * AJAX-тоггл: деактивация проставляет deactivated_at, активация — не сбрасывает (см. спеку).
   * Обновляем только статус из ответа — без рефетча, чтобы не дёргать форму и экран.
   */
  const handleToggleEnabled = async (enabled: boolean) => {
    if (!client || isToggling) return;
    setIsToggling(true);
    try {
      const res = await apiFetch(
        `/api/v1/admin/clients/${client.id}/${enabled ? 'activate' : 'deactivate'}`,
        { method: 'PATCH' },
      );
      if (!res.ok) throw new Error(await readError(res));
      const updated = (await res.json()) as Client;
      setClient((prev) =>
        prev ? { ...prev, enabled: updated.enabled, deactivatedAt: updated.deactivatedAt } : prev,
      );
      toast.success(enabled ? 'Клиент активирован' : 'Клиент деактивирован');
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось изменить статус');
      // контролируемый Switch сам вернётся: isSelected не менялся
    } finally {
      setIsToggling(false);
    }
  };

  const handleUploadLogo = async (file: File) => {
    if (!client) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiFetch(`${cardBase(client.id)}/logo`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) throw new Error(await readError(res));
      toast.success('Логотип загружен');
      await fetchClient({ silent: true });
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось загрузить логотип');
    }
  };

  const handleUploadDocument = async (file: File, kind: string) => {
    if (!client) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('kind', kind);
      const res = await apiFetch(`${cardBase(client.id)}/documents`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) throw new Error(await readError(res));
      toast.success('Документ загружен');
      await fetchClient({ silent: true });
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось загрузить документ');
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!client) return;
    try {
      const res = await apiFetch(`${cardBase(client.id)}/documents/${docId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(await readError(res));
      toast.success('Документ удалён');
      await fetchClient({ silent: true });
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Не удалось удалить документ');
    }
  };

  return {
    clientId,
    client,
    documents,
    fieldCatalog,
    form,
    login,
    loginForm,
    isLoading,
    notFound,
    isSaving,
    isToggling,
    isSavingLogin,
    setField,
    setLoginField,
    handleSave,
    handleToggleEnabled,
    handleCreateLogin,
    handleUpdateLogin,
    handleUploadLogo,
    handleUploadDocument,
    handleDeleteDocument,
  };
}
